(function () {
  'use strict';

  // Scroll-driven color reveal for the "What We Build" body copy.
  // Each word starts gray (#7d7d7d) and is interpolated toward dark (#222)
  // as the paragraph scrolls up through the viewport — earlier words fill
  // first so the copy "inks in" in reading order.
  var GRAY = [125, 125, 125]; // #7d7d7d
  var DARK = [34, 34, 34]; // #222222

  // Reveal window, as fractions of viewport height:
  //   p = 0 when the paragraph top sits at 90% of the viewport (just entering),
  //   p = 1 when its bottom reaches 45% (a little past centre).
  var START_TOP = 0.9;
  var END_BOTTOM = 0.45;

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  function lerp(a, b, t) {
    return Math.round(a + (b - a) * t);
  }

  function colorFor(t) {
    return (
      'rgb(' +
      lerp(GRAY[0], DARK[0], t) +
      ',' +
      lerp(GRAY[1], DARK[1], t) +
      ',' +
      lerp(GRAY[2], DARK[2], t) +
      ')'
    );
  }

  class SaWhatWeBuildReveal extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.words = this._wrapWords(this);
      if (!this.words.length) return;

      // Color change isn't vestibular motion, but honour the preference by
      // skipping the scroll binding and showing the final high-contrast state.
      if (reduceMotion) {
        this._paint(1);
        return;
      }

      var self = this;
      this._bound = false;
      this._onScroll = function () {
        self._request();
      };

      this._io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            self._bind();
          } else {
            self._unbind();
          }
        }
      });
      this._io.observe(this);

      this._update();
    }

    disconnectedCallback() {
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
      this._unbind();
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    }

    _bind() {
      if (this._bound) return;
      this._bound = true;
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });
      this._request();
    }

    _unbind() {
      if (!this._bound) return;
      this._bound = false;
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
    }

    _request() {
      if (this._raf) return;
      var self = this;
      this._raf = requestAnimationFrame(function () {
        self._raf = null;
        self._update();
      });
    }

    _update() {
      var rect = this.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var denom = (START_TOP - END_BOTTOM) * vh + rect.height;
      var p = denom > 0 ? (START_TOP * vh - rect.top) / denom : 0;
      this._paint(clamp(p, 0, 1));
    }

    _paint(p) {
      var n = this.words.length;
      var edge = n * p; // words before this index are fully dark
      for (var i = 0; i < n; i++) {
        this.words[i].style.color = colorFor(clamp(edge - i, 0, 1));
      }
    }

    // Wrap each visible word in a <span> we can recolor, preserving the
    // existing markup (paragraphs, inline spans, links) and whitespace.
    _wrapWords(root) {
      var doc = root.ownerDocument;
      var walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
      var textNodes = [];
      var node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue && node.nodeValue.trim()) textNodes.push(node);
      }

      var words = [];
      textNodes.forEach(function (textNode) {
        var parent = textNode.parentNode;
        if (!parent) return;
        var parts = textNode.nodeValue.split(/(\s+)/);
        var frag = doc.createDocumentFragment();
        parts.forEach(function (part) {
          if (part === '') return;
          if (/^\s+$/.test(part)) {
            frag.appendChild(doc.createTextNode(part));
            return;
          }
          var span = doc.createElement('span');
          span.className = 'sa-what-we-build__word';
          span.textContent = part;
          frag.appendChild(span);
          words.push(span);
        });
        parent.replaceChild(frag, textNode);
      });
      return words;
    }
  }

  if (!customElements.get('sa-what-we-build-reveal')) {
    customElements.define('sa-what-we-build-reveal', SaWhatWeBuildReveal);
  }

  // Nudge inline media videos to autoplay. Muted + playsinline autoplay works
  // natively in modern browsers, but iOS Safari can stall until play() is called.
  // Desktop and mobile each render their own video; only the visible one plays
  // (the other slot is display:none per breakpoint), and it re-syncs on resize.
  function syncVideos() {
    var videos = document.querySelectorAll('.sa-what-we-build__video');
    for (var i = 0; i < videos.length; i++) {
      var v = videos[i];
      // offsetParent is null when the element (or its slot) is display:none.
      if (v.offsetParent === null) {
        v.pause();
        continue;
      }
      v.muted = true;
      var p = v.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncVideos);
  } else {
    syncVideos();
  }

  var wwbMql = window.matchMedia('(max-width: 768px)');
  if (wwbMql.addEventListener) wwbMql.addEventListener('change', syncVideos);
  else if (wwbMql.addListener) wwbMql.addListener(syncVideos);
})();
